// Shared confirm modal — replaces window.confirm() for actions with
// destructive or non-trivial consequence. Click outside or Esc dismisses.

import { useEffect } from 'react';
import { Button } from '@/components/primitives/Button';
import { cn } from '@/lib/cn';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warn' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const variant = tone === 'danger' ? 'danger' : tone === 'warn' ? 'warm' : 'primary';
  const headTone =
    tone === 'danger' ? 'text-crimson' :
    tone === 'warn'   ? 'text-citrus'   :
                        'text-obsidian';

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-obsidian/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-md bg-frost p-5 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h3 className={cn('font-display text-display-sm font-semibold', headTone)}>{title}</h3>
        {body && <p className="mt-2 text-body-sm text-slate whitespace-pre-line">{body}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button variant={variant} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
